import React, { useState, useMemo, useCallback, memo } from "react";
import { PopularTrip } from "@/types";
import { ShareButton } from "@/components/AddShareLike";
import { AddButton } from "@/components/AddShareLike";
import { LikeButton } from "@/components/AddShareLike";
import { AvatarImage } from "@/components/ui/DefaultAvatar";
import LazyLoad from "@tripbuilder/image/LazyLoad";
import { formatImageUrl, getFlagImage } from "@/lib/general";
import { useNavigate } from "react-router-dom";
import useTrip from "@/stores/tripBuilder/useTrip";
import { FrownIcon } from "lucide-react";

const EngagementButton = memo(
  ({
    onClick,
    children,
    count,
    className = "",
    showCount = false,
  }: {
    onClick: () => void;
    children: React.ReactNode;
    count: number;
    className?: string;
    showCount?: boolean;
  }) => (
    <button
      className={`group relative flex items-center rounded-md py-0.5 transition-colors duration-150 ease-in-out ${className}`}
      onClick={onClick}
    >
      <div className="w-6 h-8 flex items-center justify-center">{children}</div>
      <span className="text-[13px] text-slate-800 font-medium tabular-nums min-w-[1rem] text-center group-hover:text-black">
        {showCount && count}
      </span>
    </button>
  )
);

EngagementButton.displayName = "EngagementButton";

interface ExploreTripsLHSProps {
  onTripSelect: (trip: PopularTrip) => void;
  trips?: { success: boolean; message: string; data: PopularTrip[] };
  isLoading: boolean;
  error: Error | null;
  selectedTripId?: string | null;
}

const ExploreTripsLHS: React.FC<ExploreTripsLHSProps> = ({
  onTripSelect,
  trips,
  isLoading,
  error,
  selectedTripId,
}) => {
  const { copyTrip, recordEngagement } = useTrip();
  if (isLoading) {
    return (
      <div className="w-[60%] bg-white p-2 rounded-lg h-[calc(100vh-8rem)] -translate-x-1">
        <div className="h-full overflow-y-auto no-scrollbar">
          <div className="grid grid-cols-2 gap-4 py-1 px-1">
            {Array.from({ length: 4 }).map((_, index) => (
              <div
                key={index}
                className="relative w-full rounded-xl overflow-hidden flex-shrink-0 border border-neutral-200 bg-white"
              >
                <div className="animate-pulse">
                  <div className="relative">
                    <div
                      className="w-full h-48 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse"
                      style={{ backgroundSize: "200% 100%" }}
                    ></div>

                    <div className="absolute top-2 left-2 z-10 flex flex-row items-center gap-2">
                      <div
                        className="bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse w-20 h-5 rounded-full"
                        style={{ backgroundSize: "200% 100%" }}
                      ></div>
                      <div
                        className="bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse w-16 h-5 rounded-full"
                        style={{ backgroundSize: "200% 100%" }}
                      ></div>
                    </div>
                  </div>

                  <div className="px-3 pt-3 pb-2.5">
                    <div
                      className="h-5 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse rounded w-3/4 mb-3"
                      style={{ backgroundSize: "200% 100%" }}
                    ></div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div
                          className="w-5 h-5 rounded-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse"
                          style={{ backgroundSize: "200% 100%" }}
                        ></div>
                        <div
                          className="w-20 h-3 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse rounded"
                          style={{ backgroundSize: "200% 100%" }}
                        ></div>
                      </div>

                      <div className="flex items-center space-x-4">
                        <div
                          className="w-6 h-6 rounded-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse"
                          style={{ backgroundSize: "200% 100%" }}
                        ></div>
                        <div
                          className="w-6 h-6 rounded-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse"
                          style={{ backgroundSize: "200% 100%" }}
                        ></div>
                        <div
                          className="w-6 h-6 rounded-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse"
                          style={{ backgroundSize: "200% 100%" }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-[60%] bg-white p-4 rounded-lg h-[calc(100vh-8rem)] -translate-x-1 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto py-8 px-4">
          <div className="bg-red-50 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Unable to Load Trips
          </h3>
          <p className="text-gray-600 mb-4">
            We encountered a problem while loading trips. Please try again
            later.
          </p>
          <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700 font-mono overflow-auto max-h-24">
            {error.message}
          </div>
          <button
            onClick={() => window.location.reload()}
            className="mt-6 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  if (!trips || trips.data.length === 0) {
    return (
      <div className="w-full bg-white p-4 rounded-lg h-[calc(100vh-8rem)] -translate-x-1 flex items-center justify-center">
        <div className="flex flex-col items-center justify-center h-full">
          <div className="bg-neutral-50 rounded-full p-4 mb-4">
            <FrownIcon className="w-8 h-8 text-neutral-500" strokeWidth={1.5} />
          </div>
          <h3 className="text-lg font-medium text-neutral-900 mb-2">
            No trips found
          </h3>
          <p className="text-neutral-500 text-center text-sm max-w-xs font-light">
            Check back later for trips!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-[60%] bg-white p-2 rounded-lg h-[calc(100vh-8rem)] -translate-x-1">
      <div className="h-full overflow-y-auto no-scrollbar">
        <div className="grid grid-cols-2 gap-4 py-1 px-1">
          {trips?.data?.map((trip: PopularTrip) => (
            <div key={trip.trip_id}>
              <ExploreTripsCard
                trip={trip}
                onSelect={() => onTripSelect(trip)}
                isSelected={selectedTripId === trip.trip_id}
                copyTrip={copyTrip}
                recordEngagement={recordEngagement}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

interface ExploreTripsCardProps {
  trip: PopularTrip;
  onSelect: () => void;
  isSelected: boolean;
  copyTrip: (tripId: string) => Promise<void>;
  recordEngagement: (
    tripId: string,
    action: "like" | "unlike" | "share" | "view",
    engagementData: Record<string, string>
  ) => Promise<void>;
}

const ExploreTripsCard: React.FC<ExploreTripsCardProps> = ({
  trip,
  onSelect,
  isSelected,
  copyTrip,
  recordEngagement,
}) => {
  const [shareCount, setShareCount] = useState(0);
  const [localUserLiked, setLocalUserLiked] = useState(false);
  const [localLikeCount, setLocalLikeCount] = useState(0);
  const colours = ["indigo", "blue", "green", "yellow", "red"];
  const randomColour = colours[Math.floor(Math.random() * colours.length)];
  const navigate = useNavigate();

  const handleShare = (newCount: number) => {
    if (shareCount === 0) {
      setShareCount(1);
    } else {
      setShareCount(0);
    }
  };

  const handleCopy = useCallback(async () => {
    try {
      await copyTrip(trip.trip_id);
    } catch (error) {
      console.error("Failed to copy trip:", error);
    }
  }, [trip.trip_id, copyTrip]);

  const handleLike = useCallback(async () => {
    const action = localUserLiked ? "unlike" : "like";
    setLocalUserLiked(!localUserLiked);
    setLocalLikeCount((prevCount) =>
      localUserLiked ? prevCount - 1 : prevCount + 1
    );

    try {
      await recordEngagement(trip.trip_id, action, {
        trip_id: trip.trip_id,
        owner_id: trip.participants[0]?.user_id || "",
      });
    } catch (error) {
      // Revert on error
      setLocalUserLiked(localUserLiked);
      setLocalLikeCount(localLikeCount);
      console.error("Failed to update like:", error);
    }
  }, [
    localUserLiked,
    localLikeCount,
    trip.trip_id,
    recordEngagement,
    trip.participants,
  ]);

  const getTripDetails = useMemo(() => {
    if (!trip.locations || trip.locations.length === 0) {
      return {
        locations: [],
        numberOfDays: 0,
        estimatedCost: 0,
      };
    }

    const locations = trip.locations
      .filter((loc) => !loc.origin)
      .map((loc) => ({
        name: loc.name || loc.address_components?.city || "Unknown Location",
        country: loc.address_components?.country || "Unknown",
        countryCode:
          (loc.address_components as any)?.country_code?.toLowerCase() ||
          "unknown",
      }));

    const startDate = new Date(
      Math.min(
        ...trip.locations.map((loc) =>
          new Date(loc.date?.start_date || 0).getTime()
        )
      )
    );
    const endDate = new Date(
      Math.max(
        ...trip.locations.map((loc) =>
          new Date(loc.date?.end_date || 0).getTime()
        )
      )
    );
    const numberOfDays = Math.max(
      Math.ceil(
        (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
      ) + 1,
      0
    );
    const totalCost = Math.round(
      trip.locations.reduce(
        (sum, location) => sum + (location.budget?.total || 0),
        0
      )
    );

    return {
      locations,
      numberOfDays,
      estimatedCost: totalCost,
      startDate,
      endDate,
    };
  }, [trip]);

  const handleAccountClick = () => {
    navigate(`/account/${trip.participants[0]?.user_id}`);
  };

  const tripCreator = useMemo(() => {
    return trip.participants.find((participant) => participant.creator);
  }, [trip]);

  return (
    <div
      onClick={onSelect}
      className={`relative w-full rounded-xl overflow-hidden flex-shrink-0 cursor-pointer transition-all duration-300 bg-white border ${
        isSelected
          ? "ring-2 ring-indigo-300 ring-offset-2"
          : "border-neutral-200 hover:border-neutral-300 hover:shadow-sm"
      }`}
    >
      <div className="group">
        <div className="relative">
          <div className="w-full h-48 overflow-hidden">
            {/* Locations Pill */}
            {getTripDetails.locations.length > 0 && (
              <div className="absolute top-2 left-2 z-10 max-w-[85%]">
                <div className="flex flex-row items-center gap-2">
                  {getTripDetails.locations.slice(0, 2).map((loc, index) => (
                    <div
                      key={index}
                      className="bg-white/80 backdrop-blur-sm px-2 py-1 rounded-full text-xs font-light shadow-sm flex items-center space-x-1.5"
                    >
                      <img
                        src={getFlagImage(loc.country)}
                        alt={loc.country}
                        className="w-3 h-3 object-cover rounded-full"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                      <span className="text-neutral-700 truncate max-w-[120px]">
                        {loc.name}
                      </span>
                    </div>
                  ))}
                  {getTripDetails.locations.length > 2 && (
                    <div className="bg-white/80 backdrop-blur-sm px-2 py-1 rounded-full text-xs font-light shadow-sm">
                      <span className="text-neutral-500">
                        + {getTripDetails.locations.length - 2}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="w-full h-full">
              {trip.images?.[0] ? (
                <LazyLoad
                  src={formatImageUrl(trip.images[0], "high")}
                  lowResSrc={formatImageUrl(trip.images[0], "low")}
                  alt={trip.name}
                  className="w-full h-full object-cover transition duration-700 ease-in-out group-hover:scale-105"
                  objectFit="cover"
                />
              ) : (
                <div
                  className={`w-full h-full bg-gradient-to-br from-${randomColour}-400 to-${randomColour}-600 flex items-center justify-center`}
                >
                  <span className="text-white text-3xl font-semibold">
                    {trip.name?.[0]?.toUpperCase() || "T"}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="px-3 pt-3 pb-2.5">
        <div className="flex justify-between items-start">
          <h3 className="font-semibold text-base text-neutral-800 line-clamp-1 flex-1">
            {trip.name || "Unnamed Trip"}
          </h3>
        </div>

        <div className="flex items-center justify-between">
          {/* Participant Info */}
          <button onClick={handleAccountClick} className="z-10">
            <div className="flex items-center space-x-2 backdrop-blur-sm py-1 rounded-full">
              <AvatarImage
                src={tripCreator?.profile_pic || ""}
                firstName={tripCreator?.user_name?.split(" ")[0] || "Anonymous"}
                lastName={tripCreator?.user_name?.split(" ")[1] || "Anonymous"}
                className="w-5 h-5 rounded-full"
                alt={tripCreator?.user_name || "Anonymous"}
              />
              <span className="text-xs font-light text-neutral-800">
                {tripCreator?.user_name || "Anonymous"}
              </span>
            </div>
          </button>

          <div
            className="flex items-center space-x-4"
            onClick={(e) => e.stopPropagation()}
          >
            <AddButton
              tripName={trip.name || "Unnamed Trip"}
              onAdd={handleCopy}
              compact={true}
            />
            <ShareButton
              count={shareCount}
              tripId={trip.trip_id}
              tripName={trip.name || "Unnamed Trip"}
              tripCreator={trip.participants[0]?.user_name || "Anonymous"}
              isModalOpen={false}
              setIsModalOpen={() => {}}
              showCount={false}
              className="group text-neutral-600 hover:text-neutral-800"
            />
            <LikeButton
              count={localLikeCount}
              iconClassName="fa-lg"
              userLiked={localUserLiked}
              isLiked={localUserLiked}
              className="group text-neutral-600 hover:text-neutral-800"
              onLike={handleLike}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExploreTripsLHS;
